import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DonorCombobox } from '@/components/DonorCombobox'
import { TeamPicker, useTeamMemberships, defaultTeamId } from '@/components/TeamPicker'
import { sheetFetch } from '@/lib/sheetFetch'
import type CrmFundraisingCampaign from '@/types/serializers/CrmFundraisingCampaign'
import type CrmFundraisingPaymentMethod from '@/types/serializers/CrmFundraisingPaymentMethod'

interface PrefillDonor {
  id: number
  type: string
  name: string
}

interface CreateRecurringSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  campaigns: CrmFundraisingCampaign[]
  paymentMethods: CrmFundraisingPaymentMethod[]
  /**
   * Pre-fills the donor selection when present. Used by the prefill deep-link
   * from the donor donations tab ("Nouveau don récurrent" CTA). When set
   * together with `lockDonor: true`, the donor picker renders read-only.
   */
  defaultDonor?: PrefillDonor
  lockDonor?: boolean
}

interface RecurringFormData {
  recurring_donation: {
    donor_type: string
    donor_id: string
    campaign_id: string
    payment_method_id: string
    amount_per_installment: string
    frequency: string
    start_date: string
    end_date: string
    notes: string
  }
  // Owning-team choice (US3) — top-level sibling param, not nested.
  team_id: string
}

function defaultFormData(teamId = '', campaignId = ''): RecurringFormData {
  return {
    recurring_donation: {
      donor_type: '',
      donor_id: '',
      campaign_id: campaignId,
      payment_method_id: '',
      amount_per_installment: '',
      frequency: 'monthly',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      notes: '',
    },
    team_id: teamId,
  }
}

const FREQUENCIES = [
  { value: 'monthly', label: 'Mensuel' },
  { value: 'quarterly', label: 'Trimestriel' },
  { value: 'yearly', label: 'Annuel' },
]

export function CreateRecurringSheet({
  open,
  onOpenChange,
  campaigns,
  paymentMethods,
  defaultDonor,
  lockDonor = false,
}: CreateRecurringSheetProps) {
  const memberships = useTeamMemberships()

  // Pre-select the "Hors campagne" system campaign (US6), mirroring
  // CreateDonationSheet. Resolved by the `system` flag, never by name; falls
  // back to no pre-selection if (unexpectedly) absent from the prop.
  const systemCampaignId = campaigns.find((c) => c.system)?.id
  const systemCampaignValue = systemCampaignId ? String(systemCampaignId) : ''
  const initialFormData = (): RecurringFormData => {
    const data = defaultFormData(defaultTeamId(memberships), systemCampaignValue)
    if (defaultDonor) {
      data.recurring_donation.donor_type = defaultDonor.type
      data.recurring_donation.donor_id = String(defaultDonor.id)
    }
    return data
  }

  // Local form state + a flat `errors` map keyed on the server's attribute
  // names, mirroring CreateDonationSheet (KTD3/KTD4). Submits through
  // sheetFetch (not Inertia useForm) because useForm cannot read validation
  // errors returned as a 422 body — only from redirect-delivered page props,
  // which is exactly what caused the original silent failure.
  const [formData, setFormData] = useState<RecurringFormData>(initialFormData)
  const [selectedDonor, setSelectedDonor] = useState<{ id: number; type: string; name: string } | null>(
    defaultDonor ?? null
  )
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [processing, setProcessing] = useState(false)

  function updateRecurring(patch: Partial<RecurringFormData['recurring_donation']>) {
    setFormData((prev) => ({
      ...prev,
      recurring_donation: { ...prev.recurring_donation, ...patch },
    }))
  }

  function handleDonorChange(donor: { id: number; type: string; name: string } | null) {
    setSelectedDonor(donor)
    updateRecurring({
      donor_type: donor?.type ?? '',
      donor_id: donor ? String(donor.id) : '',
    })
  }

  function resetForm() {
    setFormData(initialFormData())
    setSelectedDonor(defaultDonor ?? null)
    setErrors({})
  }

  function handleClose() {
    resetForm()
    onOpenChange(false)
  }

  // 201: clear the sheet and refresh the index list in place. Unlike the old
  // Inertia flow, success no longer navigates to the recurring donation page.
  function handleCreated() {
    resetForm()
    onOpenChange(false)
    router.reload({ only: ['recurring_donations', 'pagy'] })
  }

  async function handleResponse(response: Response) {
    if (response.status === 201) {
      handleCreated()
      return
    }
    if (response.status === 422) {
      const json = (await response.json()) as { errors?: Record<string, string> }
      setErrors(json.errors ?? {})
      return
    }
    // 200 carries duplicate-detection feedback. The recurring duplicate dialog
    // is deferred (plan KTD/U6): keep the current non-blocking behavior —
    // close the sheet and refresh rather than surfacing the matches.
    if (response.status === 200) {
      handleCreated()
      return
    }
    setErrors({ base: 'Une erreur est survenue.' })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    setErrors({})
    try {
      const response = await sheetFetch('/fundraising/recurring_donations', {
        method: 'POST',
        body: { recurring_donation: formData.recurring_donation, team_id: formData.team_id },
      })
      await handleResponse(response)
    } catch {
      setErrors({ base: 'Impossible de creer le don recurrent.' })
    } finally {
      setProcessing(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Nouveau don recurrent</SheetTitle>
          <SheetDescription>
            Enregistrez un nouveau don recurrent avec versements automatiques.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          {errors.base && (
            <p className="text-sm text-destructive">{errors.base}</p>
          )}

          {/* Owning team (multi-team users only) */}
          <TeamPicker
            value={formData.team_id}
            onChange={(val) => setFormData((prev) => ({ ...prev, team_id: val }))}
            error={errors.team_id}
            label="Equipe"
            placeholder="Choisir une equipe"
          />

          {/* Donor */}
          <div className="space-y-2">
            <Label>Donateur *</Label>
            <DonorCombobox
              value={selectedDonor}
              onChange={handleDonorChange}
              error={errors.donor || errors.donor_type}
              locked={lockDonor}
            />
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="rec_amount">Montant par versement (TND) *</Label>
            <Input
              id="rec_amount"
              type="number"
              min="0.001"
              step="0.001"
              value={formData.recurring_donation.amount_per_installment}
              onChange={(e) => updateRecurring({ amount_per_installment: e.target.value })}
              placeholder="0.000"
              required
            />
            {errors.amount_per_installment && (
              <p className="text-sm text-destructive">{errors.amount_per_installment}</p>
            )}
          </div>

          {/* Frequency */}
          <div className="space-y-2">
            <Label>Frequence *</Label>
            <Select
              value={formData.recurring_donation.frequency}
              onValueChange={(val) => updateRecurring({ frequency: val })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choisir une frequence" />
              </SelectTrigger>
              <SelectContent>
                {FREQUENCIES.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.frequency && (
              <p className="text-sm text-destructive">{errors.frequency}</p>
            )}
          </div>

          {/* Start date */}
          <div className="space-y-2">
            <Label htmlFor="rec_start_date">Date de debut *</Label>
            <Input
              id="rec_start_date"
              type="date"
              value={formData.recurring_donation.start_date}
              onChange={(e) => updateRecurring({ start_date: e.target.value })}
              required
            />
            {errors.start_date && (
              <p className="text-sm text-destructive">{errors.start_date}</p>
            )}
          </div>

          {/* End date (optional) */}
          <div className="space-y-2">
            <Label htmlFor="rec_end_date">Date de fin</Label>
            <Input
              id="rec_end_date"
              type="date"
              value={formData.recurring_donation.end_date}
              onChange={(e) => updateRecurring({ end_date: e.target.value })}
            />
            {errors.end_date && (
              <p className="text-sm text-destructive">{errors.end_date}</p>
            )}
          </div>

          {/* Campaign */}
          <div className="space-y-2">
            <Label>Campagne</Label>
            <Select
              value={formData.recurring_donation.campaign_id}
              onValueChange={(val) => updateRecurring({ campaign_id: val })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choisir une campagne" />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.campaign && (
              <p className="text-sm text-destructive">{errors.campaign}</p>
            )}
          </div>

          {/* Payment Method */}
          <div className="space-y-2">
            <Label>Mode de paiement *</Label>
            <Select
              value={formData.recurring_donation.payment_method_id}
              onValueChange={(val) => updateRecurring({ payment_method_id: val })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choisir un mode de paiement" />
              </SelectTrigger>
              <SelectContent>
                {paymentMethods.map((pm) => (
                  <SelectItem key={pm.id} value={String(pm.id)}>
                    {pm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.payment_method && (
              <p className="text-sm text-destructive">{errors.payment_method}</p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="rec_notes">Notes</Label>
            <Input
              id="rec_notes"
              value={formData.recurring_donation.notes}
              onChange={(e) => updateRecurring({ notes: e.target.value })}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={processing} className="flex-1">
              {processing ? 'Creation...' : 'Creer'}
            </Button>
            <Button type="button" variant="outline" onClick={handleClose}>
              Annuler
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
